import { Box, Typography } from '@mui/material';
import { Organization } from '_api/types';
import { AdminRouteMap } from '_constants';
import { ActionsFlexBox } from 'components/molecules';
import { ViewButtons } from 'components/molecules/types';
import { TableHeader } from 'components/organisms';
import { AddOrganization, OrganizationModal } from 'components/organisms/modals';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigateTo } from 'utils';
import { useOrganizations, useSearchResults } from 'utils/hooks';

export const ArchiveOrganizationManagement = () => {
  const { archiveOrganizations, deleteOrganization, isLoading } = useOrganizations();
  const { existingSearchTerm, updateSearchTerm } = useSearchResults();
  const [searchTerm, setSearchTerm] = useState(existingSearchTerm || '');

  const navigate = useNavigateTo();

  useEffect(() => setSearchTerm(existingSearchTerm || ''), [existingSearchTerm]);

  const filteredData = useMemo(() => {
    if (!archiveOrganizations) return [];
    if (searchTerm.trim() === '') return archiveOrganizations;
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    return archiveOrganizations.filter((org) =>
      org.name.toLowerCase().includes(lowercasedSearchTerm),
    );
  }, [searchTerm, archiveOrganizations, isLoading]);

  const [openModal, setModalOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editOrg, setEditOrg] = useState<Organization | undefined>(undefined);

  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
    updateSearchTerm(searchTerm);
  };

  const handleOpenEditModal = (row: Organization) => {
    setEditOrg(row);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setEditOrg(undefined);
  };

  const handleArchiveOnClick = (orgID: number) => {
    deleteOrganization({ orgID });
  };

  const handleOpenDetail = (org: Organization) => {
    navigate(AdminRouteMap.ARCHIVE_ORGANIZATIONS_DETAIL, org.id.toString());
  };

  const actionButtons: ViewButtons = {
    viewButton: true,
    editButton: true,
    archiveButton: false,
  };

  const formatPhoneNumber = (phoneNumber: string) => {
    const areaCode = phoneNumber.slice(0, 3);
    const firstThree = phoneNumber.slice(3, 6);
    const lastFour = phoneNumber.slice(6);
    return `(${areaCode})${firstThree}-${lastFour}`;
  };

  const tableColumns = useMemo<MRT_ColumnDef<Organization>[]>(
    () => [
      {
        header: 'Name',
        accessorFn: (row) => `${row.name}`,
      },
      {
        header: 'Address',
        enableSorting: false,
        accessorFn: (row) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
            <Typography>{row.streetAddress}</Typography>
            <Typography>{`${row.city}, ${row.state} ${row.zipCode}`}</Typography>
          </Box>
        ),
      },
      {
        header: 'Phone Number',
        enableSorting: false,
        accessorFn: (row) =>
          row && row.phoneNumber ? formatPhoneNumber(row.phoneNumber) : '',
      },
      {
        header: 'Actions',
        enableSorting: false,
        accessorFn: (row) => (
          <ActionsFlexBox
            msg={'organization'}
            viewOnClick={() => handleOpenDetail(row)}
            archiveOnClick={() => handleArchiveOnClick(row.id)}
            editOnClick={() => handleOpenEditModal(row)}
            actionButtons={actionButtons}
          />
        ),
        size: 10,
      },
    ],
    [archiveOrganizations],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <TableHeader
          value={searchTerm}
          onAddClick={() => setModalOpen(true)}
          handleSearch={handleSearch}
        />
      </Box>
      <Box sx={{ marginTop: 10 }}>
        {filteredData && !isLoading ? (
          <MaterialReactTable
            columns={tableColumns}
            data={filteredData}
            enableSorting={true}
            enableColumnActions={false}
            enableTopToolbar={false}
            enablePagination={true}
          />
        ) : null}
      </Box>
      <AddOrganization open={openModal} onClose={() => setModalOpen(false)} />
      {editOrg != undefined ? (
        <OrganizationModal
          isEdit={true}
          open={openEdit}
          organization={editOrg}
          onClose={handleCloseEdit}
        />
      ) : null}
    </Box>
  );
};
